import React, { useState } from 'react';
import {
  Play,
  ListOrdered,
  History,
  BarChart3,
  Database
} from 'lucide-react';
import CollectionForm from './CollectionForm';
import QueueMonitor from './QueueMonitor';
import CollectionHistory from './CollectionHistory';
import CollectionResults from './CollectionResults';
import { theme } from '../../styles/theme';


const CollectionPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('submit');
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null);


  const handleSubmitSuccess = (count: number) => {
    setAlert({
      type: 'success',
      message: `Successfully submitted ${count} collection requests`,
    });
    // Switch to queue monitor tab to see the submitted requests
    setActiveTab('queue');
    // Clear alert after 5 seconds
    setTimeout(() => setAlert(null), 5000);
  };

  const handleSubmitError = (error: string) => {
    setAlert({
      type: 'error',
      message: error,
    });
    // Clear alert after 5 seconds
    setTimeout(() => setAlert(null), 5000);
  };

  const tabs = [
    { id: 'submit', label: 'Submit Collection', icon: Play },
    { id: 'queue', label: 'Queue Monitor', icon: ListOrdered },
    { id: 'history', label: 'My Requests', icon: History },
    { id: 'results', label: 'Results', icon: BarChart3 },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1
            className="text-2xl font-bold flex items-center gap-2"
            style={{
              color: theme.colors.text.primary,
            }}
          >
            <Database
              className="w-6 h-6"
              style={{ color: theme.colors.primary.main }}
            />
            Data Collection Management
          </h1>
          <p
            className="text-sm mt-1"
            style={{ color: theme.colors.text.secondary }}
          >
            Submit collection requests for country and metric combinations, monitor the processing queue, and view historical requests.
          </p>
        </div>
      </div>

      {/* Alert */}
      {alert && (
        <div className={`mb-4 px-4 py-3 rounded-lg flex items-center justify-between ${
          alert.type === 'success'
            ? 'bg-green-50 border border-green-200 text-green-800'
            : 'bg-red-50 border border-red-200 text-red-800'
        }`}>
          <span>{alert.message}</span>
          <button
            onClick={() => setAlert(null)}
            className={`ml-4 ${alert.type === 'success' ? 'text-green-600 hover:text-green-800' : 'text-red-600 hover:text-red-800'}`}
          >
            ×
          </button>
        </div>
      )}

      {/* Tab Navigation */}
      <div
        className="flex space-x-1 p-1 rounded-lg"
        style={{
          backgroundColor: theme.colors.background.default,
        }}
      >
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className="flex-1 px-4 py-2 rounded-lg transition-colors flex items-center justify-center space-x-2"
              style={{
                backgroundColor:
                  activeTab === tab.id
                    ? theme.colors.background.paper
                    : "transparent",
                color:
                  activeTab === tab.id
                    ? theme.colors.primary.main
                    : theme.colors.text.secondary,
                boxShadow:
                  activeTab === tab.id ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
              }}
            >
              <Icon className="w-4 h-4" />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div
        className="rounded-xl p-6"
        style={{
          backgroundColor: theme.colors.background.paper,
          boxShadow: theme.widget.boxShadow,
        }}
      >
        {activeTab === 'submit' && (
          <CollectionForm
            onSubmitSuccess={handleSubmitSuccess}
            onSubmitError={handleSubmitError}
          />
        )}

        {activeTab === 'queue' && <QueueMonitor />}

        {activeTab === 'history' && <CollectionHistory />}

        {activeTab === 'results' && <CollectionResults />}
      </div>
    </div>
  );
};

export default CollectionPage;